/**
 * @file Layout.tsx
 * @description This file defines the main Layout component for the application.
 * It includes the header, navigation links, and a placeholder for page content.
 * It also handles responsive navigation and displays user authentication status.
 */

import React, { useState, useEffect, useRef, RefObject } from "react";
import { Outlet, NavLink, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./Layout.module.css";

import { useAuth } from "../../auth/authProvider";
import { IconButton } from "@fluentui/react";

/**
 * @component Layout
 * @description The main layout component that wraps all pages.
 * It provides a consistent header with navigation, a title, and authentication controls.
 * The `<Outlet />` component renders the content of the current route.
 */
const Layout = () => {
    // Hook for internationalization.
    const { t } = useTranslation();
    // Hook to get authentication details and functions.
    const { isAuthenticated, isLocalAuth, isSocialAuth, username, logout } = useAuth();
    // State to manage the visibility of the mobile navigation menu.
    const [menuOpen, setMenuOpen] = useState(false);
    // Ref to the menu container DOM element, used for detecting clicks outside the menu.
    const menuRef: RefObject<HTMLDivElement> = useRef(null);

    /**
     * @function toggleMenu
     * @description Toggles the `menuOpen` state to show or hide the navigation menu.
     */
    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    /**
     * @function handleClickOutside
     * @description Closes the menu if a click is detected outside of the menu's container.
     * @param {MouseEvent} event - The mouse event.
     */
    const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setMenuOpen(false);
        }
    };

    useEffect(() => {
        /**
         * @hook useEffect
         * @description Adds or removes a 'mousedown' event listener to the document
         * to handle closing the menu when clicking outside of it.
         */
        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [menuOpen]);

    return (
        <div className={styles.layout}>
            <header className={styles.header} role={"banner"}>
                <div className={styles.headerContainer} ref={menuRef}>
                    <Link to="/" className={styles.headerTitleContainer}>
                        <img src="https://www.vinci-energies.ma/app/uploads/sites/81/2021/09/Actemium-1.png" alt="Actemium" className={styles.headerLogo} />
                    </Link>
                    <nav role="navigation">
                        <ul className={`${styles.headerNavList} ${menuOpen ? styles.show : ""}`}>
                            {/* Navigation intentionally hidden for landing-only mode. */}
                        </ul>
                    </nav>
                    <div className={styles.loginMenuContainer}>
                        {isAuthenticated && (
                            <div className={styles.authActions}>
                                {isLocalAuth ? (
                                    <Link to="/profile" className={styles.profileLink}>
                                        {username}
                                    </Link>
                                ) : (
                                    // This will now correctly apply to both MSAL and Google logins
                                    <span className={styles.username}>{username}</span>
                                )}
                                <button className={styles.logoutButton} onClick={logout}>
                                    {t("logout")}
                                </button>
                            </div>
                        )}
                        <IconButton
                            iconProps={{ iconName: "GlobalNavButton" }}
                            className={styles.menuToggle}
                            onClick={toggleMenu}
                            ariaLabel={t("labels.toggleMenu")}
                        />
                    </div>
                </div>
            </header>

            {/* Renders the child route's component */}
            <Outlet />
        </div>
    );
};

export default Layout;
